type TimerHandle = ReturnType<typeof setTimeout>;

export interface JointSliderStream {
  enqueue: (value: number) => void;
  acknowledge: () => void;
  flushFinal: (value: number) => void;
  cancel: () => void;
}

interface JointSliderStreamOptions {
  send: (value: number) => void;
  timeoutMs?: number;
  setTimer?: (handler: () => void, timeoutMs: number) => TimerHandle;
  clearTimer?: (handle: TimerHandle) => void;
}

const DEFAULT_TIMEOUT_MS = 300;

export function createJointSliderStream(options: JointSliderStreamOptions): JointSliderStream {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const setTimer = options.setTimer ?? setTimeout;
  const clearTimer = options.clearTimer ?? clearTimeout;

  let inFlight = false;
  let pendingValue: number | null = null;
  let timeoutHandle: TimerHandle | null = null;

  const clearInFlightTimeout = () => {
    if (timeoutHandle !== null) {
      clearTimer(timeoutHandle);
      timeoutHandle = null;
    }
  };

  const dispatch = (value: number) => {
    options.send(value);
    inFlight = true;
    clearInFlightTimeout();
    timeoutHandle = setTimer(() => {
      if (!inFlight) {
        return;
      }
      releaseInFlight();
    }, timeoutMs);
  };

  const releaseInFlight = () => {
    clearInFlightTimeout();
    if (pendingValue === null) {
      inFlight = false;
      return;
    }
    const nextValue = pendingValue;
    pendingValue = null;
    dispatch(nextValue);
  };

  return {
    enqueue: (value: number) => {
      if (!inFlight) {
        dispatch(value);
        return;
      }
      pendingValue = value;
    },
    acknowledge: () => {
      if (!inFlight) {
        return;
      }
      releaseInFlight();
    },
    flushFinal: (value: number) => {
      clearInFlightTimeout();
      inFlight = false;
      pendingValue = null;
      options.send(value);
    },
    cancel: () => {
      clearInFlightTimeout();
      inFlight = false;
      pendingValue = null;
    },
  };
}
